import { useEffect, useState, type ReactNode } from "react";
import { InputDialog } from "./InputDialog";
import { ConfirmDialog } from "./ConfirmDialog";

/*
 * Imperative, promise-returning wrappers over InputDialog/ConfirmDialog so call
 * sites can drop in `await promptDialog(...)` / `await confirmDialog(...)` exactly
 * where they used the blocking, unstyleable native `window.prompt`/`window.confirm`.
 * Mount <DialogHost/> once near the app root. If it isn't mounted (fallback), we
 * degrade to the native dialog rather than hang.
 */

type PromptReq = {
  kind: "prompt";
  title: ReactNode;
  label: string;
  defaultValue?: string;
  confirmLabel?: string;
  validate?: (v: string) => string | null;
  resolve: (v: string | null) => void;
};
type ConfirmReq = {
  kind: "confirm";
  title: ReactNode;
  body?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  resolve: (v: boolean) => void;
};
type Req = PromptReq | ConfirmReq;

let emit: ((r: Req) => void) | null = null;

export function promptDialog(opts: {
  title: ReactNode;
  label?: string;
  defaultValue?: string;
  confirmLabel?: string;
  validate?: (v: string) => string | null;
}): Promise<string | null> {
  return new Promise((resolve) => {
    if (!emit) {
      const t = typeof opts.title === "string" ? opts.title : "";
      resolve(window.prompt(t, opts.defaultValue ?? "")); // fallback: host not mounted
      return;
    }
    emit({
      kind: "prompt",
      title: opts.title,
      label: opts.label ?? "Value",
      defaultValue: opts.defaultValue,
      confirmLabel: opts.confirmLabel,
      validate: opts.validate,
      resolve,
    });
  });
}

export function confirmDialog(opts: {
  title: ReactNode;
  body?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
}): Promise<boolean> {
  return new Promise((resolve) => {
    if (!emit) {
      const t = typeof opts.title === "string" ? opts.title : "";
      resolve(window.confirm(t)); // fallback: host not mounted
      return;
    }
    emit({ kind: "confirm", title: opts.title, body: opts.body, confirmLabel: opts.confirmLabel, danger: opts.danger, resolve });
  });
}

/** Renders the active imperative dialog. Mount once near the app root. */
export function DialogHost() {
  const [req, setReq] = useState<Req | null>(null);
  useEffect(() => {
    emit = (r) => setReq(r);
    return () => { emit = null; };
  }, []);
  if (!req) return null;

  if (req.kind === "prompt") {
    return (
      <InputDialog
        open
        onClose={() => { req.resolve(null); setReq(null); }}
        title={req.title}
        label={req.label}
        defaultValue={req.defaultValue}
        confirmLabel={req.confirmLabel}
        validate={req.validate}
        onSubmit={(v) => { req.resolve(v); setReq(null); }}
      />
    );
  }
  return (
    <ConfirmDialog
      open
      onClose={() => { req.resolve(false); setReq(null); }}
      title={req.title}
      body={req.body}
      confirmLabel={req.confirmLabel}
      danger={req.danger}
      onConfirm={() => { req.resolve(true); setReq(null); }}
    />
  );
}
